/**
 * Dessins des circuits décoratifs, portés de la maquette (js/circuits.js) :
 * pingouins cyborgs et flocons en circuit imprimé, et nappe de pistes du pied
 * de page. Tout est calculé une fois, de façon déterministe (générateur à
 * graine fixe) : le rendu est identique d'une visite à l'autre.
 */

const pad = (x: number, y: number, r = 2.4) => `<circle cx="${String(x)}" cy="${String(y)}" r="${String(r)}"/>`;
const via = (x: number, y: number) =>
  `<circle cx="${String(x)}" cy="${String(y)}" r="2.8"/><circle cx="${String(x)}" cy="${String(y)}" r="1" class="h"/>`;
const sig = (d: string) => `<path class="sig" d="${d}"/>`;

/** Pingouin cyborg de face. */
const penA = (() => {
  let s = '<path d="M50 10 C32 10 24 30 24 52 C24 76 34 90 50 90 C66 90 76 76 76 52 C76 30 68 10 50 10 Z"/>';
  s += '<path d="M50 4 V10"/>' + via(50, 3);
  s +=
    '<path d="M31 22 H69 L66 32 H34 Z" class="v"/><circle cx="41" cy="27" r="3" class="g eye"/><circle cx="59" cy="27" r="3" class="g eye"/><path d="M45 27 H55" class="t"/>';
  s += '<path d="M47 35 H53 L50 40 Z" class="f"/>';
  s += '<path d="M50 44 C40 44 35 56 35 66 C35 79 41 85 50 85 C59 85 65 79 65 66 C65 56 60 44 50 44 Z" class="b"/>';
  s +=
    '<rect x="43" y="52" width="14" height="12" rx="1.5" class="f"/><path d="M45 52 V48 M50 52 V47 M55 52 V48 M45 64 V68 M55 64 V68 M43 56 H39 M43 60 H39 M57 56 H61 M57 60 H61"/>' +
    pad(50, 47, 1.4);
  s +=
    '<path d="M45 68 V74 L41 78"/><path d="M55 68 V74 L59 78"/>' +
    pad(41, 78, 1.6) +
    pad(59, 78, 1.6) +
    '<path d="M50 64 V80"/>' +
    via(50, 80);
  s += '<path d="M25 46 L14 54 V66"/><rect x="11" y="56" width="6" height="4" rx="1" class="f"/><path d="M14 66 H8"/>' + via(8, 66);
  s += '<path d="M75 46 L86 54 V66"/><rect x="83" y="56" width="6" height="4" rx="1" class="f"/><path d="M86 66 H92"/>' + via(92, 66);
  s += '<path d="M28 40 H33 M28 58 H33 M67 40 H72 M67 58 H72" class="t"/>';
  s += '<path d="M42 90 V95 H36 M58 90 V95 H64"/>' + pad(36, 95, 1.8) + pad(64, 95, 1.8);
  s += sig('M50 3 V10 C32 10 24 30 24 52') + sig('M50 47 V52 M50 64 V80');
  return s;
})();

/** Pingouin cyborg de profil. */
const penB = (() => {
  let s = '<path d="M44 12 C30 12 24 28 24 48 C24 74 32 90 48 90 C64 90 72 76 70 56 C68 38 63 28 57 20 C54 15 50 12 44 12 Z"/>';
  s += '<path d="M57 20 L72 24 L60 30 Z" class="f"/><path d="M60 24 H68" class="t"/>';
  s += '<circle cx="50" cy="23" r="5" class="v"/><circle cx="50" cy="23" r="2.4" class="g eye"/>';
  s += '<path d="M30 30 C27 42 27 56 30 68" class="b"/><path d="M34 26 L30 36 M32 46 H26 M32 60 H26" class="t"/>';
  s +=
    '<path d="M40 40 C36 52 36 68 44 80" /><rect x="46" y="48" width="12" height="16" rx="1.5" class="f"/><path d="M58 52 H64 M58 56 H64 M58 60 H64 M46 52 H42 M46 60 H42"/>';
  s += '<path d="M52 30 V48"/>' + via(52, 30) + '<path d="M52 64 V72 L58 78"/>' + pad(58, 78, 1.6);
  s +=
    '<path d="M28 52 L16 64 V76"/>' +
    via(16, 76) +
    '<path d="M44 90 V95 H36 M58 90 V95 H66"/>' +
    pad(36, 95, 1.8) +
    pad(66, 95, 1.8);
  s += sig('M52 30 V48') + sig('M44 12 C30 12 24 28 24 48 C24 74 32 90 48 90');
  return s;
})();

/** Flocon circuit dense. */
const flkA = (() => {
  let s =
    '<polygon points="50,41 57.8,45.5 57.8,54.5 50,59 42.2,54.5 42.2,45.5" class="f"/><polygon points="50,45 54.3,47.5 54.3,52.5 50,55 45.7,52.5 45.7,47.5"/>';
  let sg = '';
  for (let k = 0; k < 6; k++) {
    const a = (k * Math.PI) / 3 - Math.PI / 2;
    const c = Math.cos(a);
    const n = Math.sin(a);
    const P = (r: number, d = 0) => [+(50 + c * r - n * d).toFixed(1), +(50 + n * r + c * d).toFixed(1)] as const;
    const [x0, y0] = P(10);
    const [x1, y1] = P(44);
    const [bx, by] = P(22);
    const [cx2, cy2] = P(32);
    const [l1x, l1y] = P(30, 8);
    const [r1x, r1y] = P(30, -8);
    const [l2x, l2y] = P(39, 6);
    const [r2x, r2y] = P(39, -6);
    const p = (x: number, y: number) => `${String(x)} ${String(y)}`;
    s += `<path d="M${p(x0, y0)} L${p(x1, y1)}"/>`;
    s += `<path d="M${p(bx, by)} L${p(l1x, l1y)} M${p(bx, by)} L${p(r1x, r1y)}"/>`;
    s += `<path d="M${p(cx2, cy2)} L${p(l2x, l2y)} M${p(cx2, cy2)} L${p(r2x, r2y)}"/>`;
    s += via(x1, y1) + pad(l1x, l1y, 1.6) + pad(r1x, r1y, 1.6) + pad(l2x, l2y, 1.2) + pad(r2x, r2y, 1.2);
    const [mx, my] = P(16, 3);
    s += `<rect x="${String(mx - 1.4)}" y="${String(my - 1.4)}" width="2.8" height="2.8" class="f"/>`;
    if (k % 2 === 0) sg += sig(`M${p(x0, y0)} L${p(x1, y1)}`);
  }
  s += '<circle cx="50" cy="50" r="47" class="ring"/>';
  return s + '<circle cx="50" cy="50" r="2" class="g eye"/>' + sg;
})();

/** Flocon hexagonal. */
const flkB = (() => {
  const hex = (r: number) =>
    Array.from({ length: 6 }, (_, k) => {
      const a = (k * Math.PI) / 3;
      return `${(50 + Math.cos(a) * r).toFixed(1)},${(50 + Math.sin(a) * r).toFixed(1)}`;
    }).join(' ');
  let s = `<polygon points="${hex(14)}" class="f"/><polygon points="${hex(26)}" class="ring"/>`;
  let sg = '';
  for (let k = 0; k < 6; k++) {
    const a = (k * Math.PI) / 3;
    const c = Math.cos(a);
    const n = Math.sin(a);
    const xy = (r: number, d = 0) => [+(50 + c * r - n * d).toFixed(1), +(50 + n * r + c * d).toFixed(1)] as const;
    const p = (r: number, d = 0) => xy(r, d).join(' ');
    const d = `M${p(14)} L${p(30)} L${p(38, 7)} L${p(46, 7)}`;
    s += `<path d="${d}"/><path d="M${p(30)} L${p(44)}"/><path d="M${p(22)} L${p(27, -6)}"/>`;
    s += via(...xy(46, 7)) + pad(...xy(44), 2) + pad(...xy(27, -6), 1.4);
    if (k % 2) sg += sig(d);
  }
  s += '<path d="M44 50 H56 M50 44 V56" class="t"/><circle cx="50" cy="50" r="3" class="g eye"/>';
  return s + sg;
})();

/** Générateur pseudo-aléatoire à graine fixe, comme la maquette. */
function seeded(seed: number) {
  let state = seed;
  return () => (state = (state * 9301 + 49297) % 233280) / 233280;
}

export type Sticker = {
  svg: string;
  /** Position en % de la couche. */
  left: number;
  top: number;
  /** Variables CSS d'animation (taille, opacité, dérive, rotation…). */
  vars: Record<string, string>;
  delay: string;
};

const DESIGNS = [penA, flkA, penB, flkB, penA, flkB, penB, flkA, penA, flkA];
const SLOTS: [number, number][] = [
  [3, 5], [90, 12], [7, 24], [92, 33], [4, 46], [89, 55], [7, 67], [91, 76], [4, 88], [88, 94],
];

export const stickers: Sticker[] = (() => {
  const rnd = seeded(11);
  return SLOTS.map(([left, top], i) => {
    const svg = DESIGNS[i];
    const isPeng = svg === penA || svg === penB;
    const size = isPeng ? 46 + Math.round(rnd() * 12) : 38 + Math.round(rnd() * 14);
    const vars: Record<string, string> = {
      '--s': `${String(size)}px`,
      '--o': (0.75 + rnd() * 0.25).toFixed(2),
      '--d': `${(7 + rnd() * 6).toFixed(1)}s`,
      '--dl': `${(-rnd() * 12).toFixed(1)}s`,
      '--sd': `${(2.2 + rnd() * 2.5).toFixed(1)}s`,
      '--ed': `${(3 + rnd() * 4).toFixed(1)}s`,
      '--gd': `${(6 + rnd() * 6).toFixed(1)}s`,
      '--gl': `${(-rnd() * 8).toFixed(1)}s`,
      '--dx': `${String(Math.round((rnd() - 0.5) * 150))}px`,
      '--dy': `${String(Math.round((rnd() - 0.5) * 150))}px`,
    };
    const r = isPeng ? Math.round((rnd() - 0.5) * 14) : Math.round((rnd() - 0.5) * 40);
    vars['--r0'] = `${String(r)}deg`;
    vars['--r1'] = `${String(r + Math.round((rnd() - 0.5) * (isPeng ? 30 : 120)))}deg`;
    return { svg, left, top, vars, delay: `${(i * 0.08).toFixed(2)}s, var(--dl)` };
  });
})();

/** Nappe de pistes du pied de page, 1440 × 420. */
export const footerCircuit: string = (() => {
  const rn = seeded(3);
  const W = 1440;
  const H = 420;
  let paths = '';
  let pads = '';
  let sigs = '';
  for (let k = 0; k < 18; k++) {
    const left = k % 2 === 0;
    let y = 20 + Math.round((rn() * (H - 40)) / 12) * 12;
    let x = left ? 0 : W;
    let d = `M${String(x)} ${String(y)}`;
    const steps = 2 + Math.floor(rn() * 3);
    for (let st = 0; st < steps; st++) {
      const run = 80 + Math.round(rn() * 220);
      x += left ? run : -run;
      d += ` H${String(x)}`;
      if (st < steps - 1) {
        const dy = (rn() < 0.5 ? -1 : 1) * (24 + Math.round(rn() * 3) * 12);
        x += left ? Math.abs(dy) : -Math.abs(dy);
        y = Math.max(16, Math.min(H - 16, y + dy));
        d += ` L${String(x)} ${String(y)}`;
      }
    }
    paths += `<path d="${d}"/>`;
    pads += `<circle cx="${String(x)}" cy="${String(y)}" r="5"/><circle cx="${String(x)}" cy="${String(y)}" r="1.8" class="h"/>`;
    if (k % 3 === 0) {
      sigs += `<path class="sig" style="animation-duration:${(4 + rn() * 5).toFixed(1)}s;animation-delay:-${(rn() * 6).toFixed(1)}s" d="${d}"/>`;
    }
  }
  const chips = (
    [
      [300, 120, 70, 46],
      [1080, 250, 90, 54],
      [700, 40, 56, 40],
    ] as const
  )
    .map(([cx, cy, w, h]) => {
      let s = `<rect x="${String(cx)}" y="${String(cy)}" width="${String(w)}" height="${String(h)}" rx="5" class="c"/>`;
      for (let p = 0; p < Math.floor(w / 14); p++) {
        const px = cx + 10 + p * 14;
        s += `<path d="M${String(px)} ${String(cy)} V${String(cy - 12)} M${String(px)} ${String(cy + h)} V${String(cy + h + 12)}"/>`;
      }
      return s;
    })
    .join('');
  return paths + chips + pads + sigs;
})();
